import { CalendarIcon } from 'lucide-react'
import { useState } from 'react'
import { useToggle } from '../../hooks/use-toggle'
import { Button, type ButtonProps } from './button'
import { Calendar } from './calendar'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

/**
 * Contrôlé dès que le parent passe `selected`, même `undefined` : une date qu'il a désélectionnée
 * ne doit pas laisser l'ancienne affichée.
 */
export const isDatePickerControlled = (props: { selected?: Date }): boolean => 'selected' in props

/** Date affichée : celle du parent en mode contrôlé, sinon la dernière choisie. */
export const datePickerDisplayedDate = (
  props: { selected?: Date },
  unControlledDate: Date | undefined,
): Date | undefined => (isDatePickerControlled(props) ? props.selected : unControlledDate)

export const DatePicker = ({
  className,
  onSelect,
  placeholder,
  variant = 'outline',
  disabled = false,
  trigger,
  triggerContent,
  align = 'start',
  ...props
}: Extract<React.ComponentProps<typeof Calendar>, { mode: 'single' }> & {
  placeholder?: string
  variant?: ButtonProps['variant']
  disabled?: boolean
  /**
   * Élément déclencheur (`render` de Base UI), le `Button` partagé par défaut. Remplace aussi
   * `variant` et `disabled` : le déclencheur fourni porte lui-même son état désactivé.
   */
  trigger?: React.ReactElement
  /** Contenu du déclencheur, la date `fr-FR` et l'icône calendrier par défaut. */
  triggerContent?: (date: Date | undefined) => React.ReactNode
  /** Alignement du calendrier sous le déclencheur. */
  align?: React.ComponentProps<typeof PopoverContent>['align']
}) => {
  const [open, toggleOpen] = useToggle()
  const [unControlledDate, setUnControlledDate] = useState<Date>()

  const date = datePickerDisplayedDate(props, unControlledDate)

  return (
    <Popover open={open} onOpenChange={toggleOpen}>
      <PopoverTrigger
        id={props.id}
        render={
          trigger ?? <Button variant={variant} disabled={disabled} className="w-full rounded-lg" />
        }
        className={className}
      >
        {triggerContent === undefined ? (
          <div className="relative w-full flex items-center">
            <span className="block w-full text-left">
              {date ? date.toLocaleDateString('fr-FR') : (placeholder ?? 'Sélectionner une date')}
            </span>
            <span className="pointer-events-none absolute right-1 flex items-center">
              <CalendarIcon className="size-4 shrink-0 text-muted-foreground" />
            </span>
          </div>
        ) : (
          triggerContent(date)
        )}
      </PopoverTrigger>
      <PopoverContent className="w-auto overflow-hidden p-0" align={align}>
        <Calendar
          {...props}
          mode="single"
          selected={date}
          onSelect={(newDate, ...args) => {
            if (!isDatePickerControlled(props)) {
              setUnControlledDate(newDate)
            }
            onSelect?.(newDate as Date, ...args)
            toggleOpen()
          }}
        />
      </PopoverContent>
    </Popover>
  )
}
